import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { FileIcon, Download, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ImageLightbox } from '../components/ImageLightbox';
import { EvoluxIconSmall } from '../components/EvoluxIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PublicAnalysisView = () => {
  const { token } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Image lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedAltText, setSelectedAltText] = useState('');

  useEffect(() => {
    fetchPublicAnalysis();
  }, [token]);

  // Helper functions for lightbox
  const openLightbox = (url, altText) => {
    setSelectedImageUrl(url);
    setSelectedAltText(altText || '');
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImageUrl('');
    setSelectedAltText('');
  };

  // Add click handlers to images in the public content
  useEffect(() => {
    if (!analysis) return;

    // Use a small delay to ensure DOM is ready after dangerouslySetInnerHTML
    const timeoutId = setTimeout(() => {
      const contentContainer = document.querySelector('.prose');
      if (!contentContainer) return;

      // Find all images and attach individual click handlers
      const images = contentContainer.querySelectorAll('img');
      
      images.forEach(img => {
        // Style
        img.style.cursor = 'pointer';
        img.title = 'Clique para ampliar';
        
        // Click handler
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          openLightbox(img.src, img.alt);
        };
        
        // Store handler reference for cleanup
        img.dataset.hasLightboxHandler = 'true';
        img.addEventListener('click', clickHandler);
        
        // Store handler for cleanup
        img._lightboxClickHandler = clickHandler;
      });
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      const contentContainer = document.querySelector('.prose');
      if (contentContainer) {
        const images = contentContainer.querySelectorAll('img[data-has-lightbox-handler]');
        images.forEach(img => {
          if (img._lightboxClickHandler) {
            img.removeEventListener('click', img._lightboxClickHandler);
            delete img._lightboxClickHandler;
          }
        });
      }
    };
  }, [analysis]);

  /**
   * Normalize image URLs in shared/public content.
   *
   * Strategy: convert ALL image URLs pointing to our file endpoint into
   * RELATIVE URLs (starting with "/api/..."). The browser will then resolve
   * them against the current page origin, which is exactly the same origin
   * that serves the public link itself. This is robust across:
   *   - environment changes (preview ↔ production)
   *   - custom domain switches
   *   - bundles built against an older REACT_APP_BACKEND_URL value
   *
   * Why not hardcode REACT_APP_BACKEND_URL anymore?
   * The build-time API_URL gets baked into the JS bundle. If the deploy
   * domain changes or the bundle is older than the current setup, absolute
   * URLs become stale and images break "after some time" — exactly the
   * symptom the user reported.
   */
  const normalizeImageUrls = (htmlContent) => {
    if (!htmlContent) return '';

    let result = htmlContent;

    // Pattern 1: Any absolute URL hitting our file endpoint -> make it relative
    // Example: https://old-domain/api/technical-analyses/files/abc.png
    //       -> /api/technical-analyses/files/abc.png
    const absolutePattern = /src="https?:\/\/[^"]*\/api\/technical-analyses\/files\/([^"]+)"/g;
    result = result.replace(absolutePattern, 'src="/api/technical-analyses/files/$1"');

    // Pattern 2: Legacy "/uploads/{file}" path used before the MongoDB migration
    // Re-point to the persistent API endpoint so the server-side fallback can serve it.
    const legacyUploadsPattern = /src="\/uploads\/([^"]+)"/g;
    result = result.replace(legacyUploadsPattern, 'src="/api/technical-analyses/files/$1"');

    // Relative "/api/..." URLs are already fine — browser resolves them against
    // window.location.origin (same host that served this public link).
    return result;
  };

  const fetchPublicAnalysis = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/technical-analyses/public/${token}`);
      const data = response.data;
      
      // Normalize image URLs to use current domain
      if (data.content) {
        data.content = normalizeImageUrls(data.content);
      }
      
      setAnalysis(data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError(error.response.data.detail || 'Este link de compartilhamento não está mais ativo ou é inválido.');
      } else {
        setError('Erro ao carregar análise técnica.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md p-8 text-center bg-white border-slate-200">
          <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-bold mb-2 text-slate-900">Link inválido</h2>
          <p className="text-sm text-slate-600">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="border-b bg-white border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EvoluxIconSmall size={24} />
            <div>
              <h1 className="text-lg font-bold">
                <span className="text-indigo-600">Evol</span>
                <span className="bg-gradient-to-r from-cyan-500 to-teal-400 bg-clip-text text-transparent">UX</span>
              </h1>
              <p className="text-xs text-slate-600">Visualização somente leitura</p>
            </div>
          </div>
          <Badge variant="secondary">Read-only</Badge>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Card className="p-8 bg-white border-slate-200">
          {/* Title */}
          <h1 className="text-4xl font-bold mb-6 text-slate-900">
            {analysis.title}
          </h1>

          {/* Metadata */}
          <div className="space-y-2 mb-6 pb-6 border-b border-slate-200">
            <p className="text-sm text-slate-600">
              Última atualização em {new Date(analysis.updated_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })} por {analysis.author_name}
            </p>
            
            {/* Tags */}
            {analysis.tags && analysis.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {analysis.tags.map(tag => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Content - styled with smaller font (~14px) for better readability */}
          <div 
            className="public-technical-analysis-content prose prose-sm max-w-none mb-8
              prose-headings:text-slate-900 prose-headings:font-bold
              prose-h1:text-xl prose-h1:mb-4 prose-h1:mt-8
              prose-h2:text-lg prose-h2:mb-3 prose-h2:mt-6
              prose-h3:text-base prose-h3:font-semibold prose-h3:mb-2 prose-h3:mt-4
              prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-primary prose-a:underline prose-a:cursor-pointer
              prose-strong:text-slate-900 prose-strong:font-semibold
              prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
              prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
              prose-li:text-slate-700 prose-li:mb-1
              prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-slate-600
              prose-hr:border-slate-200 prose-hr:my-6
              prose-img:max-w-full prose-img:h-auto prose-img:rounded-lg prose-img:my-4 prose-img:shadow-sm"
            dangerouslySetInnerHTML={{ __html: analysis.content }}
          />

          {/* Attachments */}
          {analysis.attachments && analysis.attachments.length > 0 && (
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-lg font-semibold mb-4 text-slate-900">Anexos</h3>
              <div className="space-y-2">
                {analysis.attachments.map(attachment => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <FileIcon className="h-5 w-5 text-primary" />
                      <span className="text-sm font-medium text-slate-900">
                        {attachment.file_name}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`${API_URL}${attachment.file_url}`, '_blank')}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Baixar
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        imageUrl={selectedImageUrl}
        altText={selectedAltText}
        onClose={closeLightbox}
      />
    </div>
  );
};
